import axios, { AxiosInstance } from 'axios';
import {
  AddressDetailResponse,
  AutoSuggestAddressApi,
  AutoSuggestAddressType,
  SuggestedAddress,
} from 'components/Address/AutoSuggestAddress/types';
import { pascalToCamelCaseDeserializer } from 'components/Utils/toCamelCase';
import { useMemo } from 'react';
import urlJoin from 'url-join';

interface AddressSearchApiOptions {
  axiosInstance?: AxiosInstance;
  baseUrl?: string;

  /** Set to `true` if the API returns its objects using pascal case */
  isPascalCase?: boolean;
}

export const useAddressSearchApi = ({
  axiosInstance,
  baseUrl,
  isPascalCase = false,
}: AddressSearchApiOptions = {}): AutoSuggestAddressApi => {
  return useMemo(() => {
    const httpClient = axiosInstance ?? axios.create();
    const getApiUrl = (relativeUrl: string) => (!!baseUrl ? urlJoin(baseUrl, relativeUrl) : relativeUrl);

    const getSuggestions = async (
      searchTerm: string,
      addressType?: AutoSuggestAddressType,
      signal?: AbortSignal
    ): Promise<SuggestedAddress[]> => {
      const params = new URLSearchParams();
      params.set('term', searchTerm);
      params.set('addressType', addressType ?? 'all');

      const { data } = await httpClient.get<SuggestedAddress[]>(getApiUrl(`/api/v3/Addresses?${params}`), { signal });

      return data;
    };

    const getDetailsById = async (id: string): Promise<AddressDetailResponse> => {
      const { data } = await httpClient.get<AddressDetailResponse>(getApiUrl(`/api/v3/Addresses/${id}`), {
        transformResponse: (json: string) => {
          const deserializer = isPascalCase ? pascalToCamelCaseDeserializer : undefined;
          return JSON.parse(json, deserializer);
        },
      });

      return data;
    };

    return { getSuggestions, getDetailsById };
  }, [axiosInstance, baseUrl, isPascalCase]);
};
